





































































































































import {
	computed,
	defineComponent,
	ref,
} from '@vue/composition-api';
import {
	ContentType,
	ContentV2,
	Discussion,
	DiscussionContentItem,
} from '@front-app/services/GroupsApi/DiscussionModel';
import { Size } from '@front-app/components/constants';
import { ButtonPalettes } from '@front-app/components/buttons/palettes';
import { Routes } from '@front-app/constants/routes';
import { ContentQueryParam } from '@front-app/modules/content/routing/params';
import { isNotNullish } from '@front-app/util/types';
import { getUsername } from '@front-app/util/user';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { UserAvatarStore } from '@front-app/store/singletons/User';
import { bytesToSize } from '@front-app/util/bytesToSize';
import { PartialUser } from '@front-app/services/UserApi/UserModel';
import { useFileDownloadV2 } from '@front-app/hooks/useFileDownload';
import { useDiscussionFollow } from '@front-app/components/discussion/useDiscussionFollow';
import UserLink from '@front-app/components/buttons/UserLink.vue';
import EditButton from '@front-app/components/buttons/concrete/EditButton.vue';
import UserAvatar from '@front-app/components/user/avatar.vue';
import CtaButton from '@front-app/components/buttons/CtaButton.vue';
import LoaderState from '@front-app/components/LoaderState.vue';
import CategoriesList from '@front-app/components/group/CategoriesList.vue';
import VideoEmbed from '@front-app/components/video/VideoEmbed.vue';
import { checkIfEmbeddedVideo } from '@front-app/components/video/util';
import { useGroupContentStore } from '@front-app/store/piniaModules/groupContent';
import { canEditContent } from '@front-app/util/permission/content';
import { syncedRef } from '@front-app/util/reactivity';
import DiscussionContentCard from './DiscussionContentCard.vue';

export default defineComponent({
	components: {
		EditButton,
		UserLink,
		UserAvatar,
		CtaButton,
		DiscussionContentCard,
		LoaderState,
		CategoriesList,
		FontAwesomeIcon,
		VideoEmbed,
	},
	props: {
		hideAttachedTo: Boolean,
		isFetchingAttachedTo: Boolean,
	},
	setup(props, ctx) {
		const groupContentStore = useGroupContentStore();
		const selectedContent = syncedRef(() => groupContentStore.getById(groupContentStore.selectedContentId)).ref;

		const getUsernameInternal = (user: PartialUser): string => getUsername(ctx.root.$t.bind(ctx.root), user);
		const fileSize = computed(() => {
			if (selectedContent.value?.type !== ContentType.FILE) {
				return '';
			}
			const fs = bytesToSize(selectedContent.value.metadata.byteSize);
			const quantity = ctx.root.$n(fs.quantity, 'short');
			return ctx.root.$t(fs.size, { quantity });
		});
		const attachedTo = computed(() => {
			if (!selectedContent.value || selectedContent.value.type === ContentType.DISCUSSION) {
				return [];
			}
			const attachments = selectedContent.value.metadata.attachedToDiscussions || [];
			return attachments
				.filter(({ messages }) => messages.length > 0)
				.map(discussionMessageToContent)
				.filter(isNotNullish);
		});
		const goToDiscussion = (discussion: ContentV2) => {
			ctx.root.$router.push({
				name: Routes.GROUP_DISCUSSION,
				params: {
					groupId: discussion.groupId,
					discussionId: discussion.id,
				},
			});
		};
		const {
			isLoading: isDownloading,
			download,
		} = useFileDownloadV2();
		const performDownload = () => selectedContent.value && download(
			selectedContent.value.groupId,
			selectedContent.value.id,
			selectedContent.value.name,
		);

		// Edit
		const openEditContentModal = () => {
			const q = ctx.root.$route.query;
			ctx.root.$router.push({ query: { ...q, [ContentQueryParam.EDIT_CONTENT]: selectedContent.value?.id } });
		};

		// TODO: Refactor the discussions/content to use Pinia
		const toggleFollow = (item: DiscussionContentItem) => {
			const { toggleLocalFollowState } = useDiscussionFollow(ref(item));
			toggleLocalFollowState();
			ctx.emit('toggle-follow', item.id);
		};

		// Embedded video
		const isEmbeddedVideo = computed(() => {
			if (!selectedContent.value) {
				return;
			}
			// eslint-disable-next-line consistent-return
			return checkIfEmbeddedVideo(selectedContent.value);
		});

		return {
			selectedContent,
			// Constants
			ContentType,
			fileSize,
			ButtonPalettes,
			Size,
			// Avatar
			getUsername: getUsernameInternal,
			getAvatar: UserAvatarStore.get,
			fetchAvatar: UserAvatarStore.fetch,
			// Discussion list
			attachedTo,
			goToDiscussion,
			// Embedded video
			isEmbeddedVideo,
			// Download
			isDownloading,
			performDownload,
			// Edit
			canEditContent,
			openEditContentModal,
			// Follow
			toggleFollow,
		};
	},
});

function discussionMessageToContent(discussion: Discussion): DiscussionContentItem {
	const firstMessage = discussion.messages[0];
	return {
		id: firstMessage.id,
		groupId: firstMessage.groupId,
		type: ContentType.DISCUSSION,
		name: firstMessage.subject,
		description: firstMessage.messageText,
		createdDateTime: new Date(firstMessage.created).toISOString(),
		categories: discussion.categories,
		creator: firstMessage.sender,
		reactions: firstMessage.reactions,
		metadata: {
			discussion,
		},
	};
}
